import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShoppingBag, User, LogOut, Menu, X, Gift } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return null;
    if (user.role === 'merchant') return { label: 'Merchant Portal', path: '/merchant' };
    if (user.role === 'admin' || user.role === 'super_admin') return { label: 'Admin Portal', path: '/admin' };
    return { label: 'My Profile', path: '/profile' };
  };

  const dashLink = getDashboardLink();

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#561D1A]/95 border-b border-white/10 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-8 h-8 bg-[#EC1A23] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Gift size={18} className="text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-white text-lg font-bold tracking-tight">LuxeMall</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/80 hover:text-white text-sm font-body transition-colors" data-testid="nav-shop">
              Shop
            </Link>
            {dashLink && (
              <Link to={dashLink.path} className="text-white/80 hover:text-white text-sm font-body transition-colors" data-testid="nav-dashboard">
                {dashLink.label}
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-3">
                <span className="text-white/60 text-sm font-body">{user.name}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm transition-colors"
                  data-testid="nav-logout"
                >
                  <LogOut size={15} />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <Link
                to="/account"
                className="flex items-center gap-1.5 bg-[#EC1A23] hover:bg-[#C4121A] text-white px-4 py-1.5 rounded-full text-sm font-body font-medium transition-all hover:shadow-lg"
                data-testid="nav-login"
              >
                <User size={14} />
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-mobile-menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 space-y-3">
            <Link to="/" onClick={() => setMenuOpen(false)} className="block text-white/80 hover:text-white text-sm py-2">Shop</Link>
            {dashLink && (
              <Link to={dashLink.path} onClick={() => setMenuOpen(false)} className="block text-white/80 hover:text-white text-sm py-2">{dashLink.label}</Link>
            )}
            {user ? (
              <button onClick={handleLogout} className="block text-white/80 hover:text-white text-sm py-2 w-full text-left">Logout</button>
            ) : (
              <Link to="/account" onClick={() => setMenuOpen(false)} className="block text-white text-sm py-2">Sign In</Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
